<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { ref, computed } from 'vue';
import { useCopy } from '@/composables/useCopy';
const props = defineProps({
    domain: Object, service: String, branding: Object,
    terms: { type: Array, default: () => [] }, grouped: { type: Object, default: () => ({}) },
    categories: { type: Array, default: () => [] }, seoTitle: { type: String, default: '' },
    metaDescription: { type: String, default: '' }, jsonLd: Object, designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const searchQuery = ref('');
const activeCategory = ref('');
const categoryLabels = { grundlagen: 'Grundlagen', technik: 'Technik', wirtschaftlichkeit: 'Wirtschaftlichkeit', 'förderung': 'Förderung', installation: 'Installation', recht: 'Recht' };

const filteredTerms = computed(() => {
    let result = props.terms;
    if (activeCategory.value) result = result.filter(item => item.category === activeCategory.value);
    if (searchQuery.value.trim()) { const q = searchQuery.value.toLowerCase(); result = result.filter(item => item.term.toLowerCase().includes(q) || item.short_definition.toLowerCase().includes(q)); }
    return result;
});
const filteredGrouped = computed(() => { const g = {}; for (const item of filteredTerms.value) { const l = item.term.charAt(0).toUpperCase(); if (!g[l]) g[l] = []; g[l].push(item); } return g; });
const letters = computed(() => Object.keys(filteredGrouped.value).sort());
</script>

<template>
    <Head :title="seoTitle || 'Glossar'"><meta :content="metaDescription" name="description" /><script v-if="jsonLd" type="application/ld+json" v-text="JSON.stringify(jsonLd)" /></Head>
    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="glossary" heroMode>
        <section class="sv-gloss-hero">
            <div class="sv-gloss-hero__bg" aria-hidden="true"></div>
            <div class="sv-gloss-hero__content">
                <span class="sv-gloss-hero__tag">{{ t('glossary_hero_tag', 'Glossar') }}</span>
                <h1 class="sv-gloss-hero__title">{{ t('glossary_hero_title', 'Fachbegriffe einfach erklärt') }}</h1>
                <p class="sv-gloss-hero__sub">{{ t('glossary_hero_sub', 'Von A wie Autarkie bis Z wie Zählerschrank - alle wichtigen Begriffe rund um Photovoltaik und Wärmepumpen.') }}</p>
            </div>
        </section>

        <section class="sv-gloss-search">
            <div class="sv-gloss-search__inner">
                <input v-model="searchQuery" type="text" :placeholder="t('glossary_search_placeholder', 'Begriff suchen...')" class="sv-gloss-search__input" />
                <div class="sv-gloss-search__cats">
                    <button @click="activeCategory = ''" class="sv-gloss-cat-btn" :class="{ 'sv-gloss-cat-btn--active': !activeCategory }">{{ t('glossary_filter_all', 'Alle') }}</button>
                    <button v-for="cat in categories" :key="cat" @click="activeCategory = cat" class="sv-gloss-cat-btn" :class="{ 'sv-gloss-cat-btn--active': activeCategory === cat }">{{ categoryLabels[cat] || cat }}</button>
                </div>
            </div>
        </section>

        <section class="sv-gloss-list">
            <div class="sv-gloss-list__inner">
                <div v-for="letter in letters" :key="letter" class="sv-gloss-group">
                    <h2 class="sv-gloss-group__letter">{{ letter }}</h2>
                    <div class="sv-gloss-group__grid">
                        <Link v-for="term in filteredGrouped[letter]" :key="term.slug" :href="`/glossar/${term.slug}`" class="sv-gloss-term-card">
                            <h3 class="sv-gloss-term-card__term">{{ term.term }}</h3>
                            <p class="sv-gloss-term-card__def">{{ term.short_definition }}</p>
                            <span v-if="term.category" class="sv-gloss-term-card__cat">{{ categoryLabels[term.category] || term.category }}</span>
                        </Link>
                    </div>
                </div>
                <div v-if="!letters.length" class="sv-gloss-empty">{{ t('glossary_empty', 'Keine Begriffe gefunden.') }}</div>
            </div>
        </section>
</AppLayout>
</template>

<style scoped>
.sv-gloss-hero { position: relative; min-height: 340px; display: flex; align-items: center; background: #0a1628; overflow: hidden; }
.sv-gloss-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628, #001d75); }
.sv-gloss-hero__content { position: relative; z-index: 2; max-width: 700px; margin: 0 auto; padding: 100px 24px 48px; text-align: center; }
.sv-gloss-hero__tag { display: inline-block; padding: 6px 18px; border-radius: 20px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #00d782; border: 1px solid rgba(0,215,130,0.3); background: rgba(0,215,130,0.08); margin-bottom: 16px; }
.sv-gloss-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(32px, 5vw, 48px); font-weight: 800; color: #fff; margin-bottom: 12px; }
.sv-gloss-hero__sub { font-family: 'Inter', sans-serif; font-size: 16px; color: rgba(255,255,255,0.65); }

.sv-gloss-search { padding: 32px 0; background: #f0f4f8; }
.sv-gloss-search__inner { max-width: 800px; margin: 0 auto; padding: 0 24px; }
.sv-gloss-search__input { width: 100%; padding: 14px 18px; border: 2px solid #e2e8f0; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; color: #1a1a1a; background: #fff; margin-bottom: 16px; }
.sv-gloss-search__input:focus { outline: none; border-color: #00d782; box-shadow: 0 0 0 3px rgba(0,215,130,0.12); }
.sv-gloss-search__cats { display: flex; flex-wrap: wrap; gap: 8px; }
.sv-gloss-cat-btn { padding: 8px 16px; border: 1px solid #e2e8f0; border-radius: 20px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #64748b; background: #fff; cursor: pointer; transition: all 150ms; }
.sv-gloss-cat-btn:hover { border-color: #00d782; }
.sv-gloss-cat-btn--active { background: #00d782; color: #1a1a1a; border-color: #00d782; }

.sv-gloss-list { padding: 48px 0 80px; background: #ffffff; }
.sv-gloss-list__inner { max-width: 900px; margin: 0 auto; padding: 0 24px; }
.sv-gloss-group { margin-bottom: 40px; }
.sv-gloss-group__letter { font-family: 'Inter', sans-serif; font-size: 32px; font-weight: 800; color: #00d782; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid rgba(0,215,130,0.15); }
.sv-gloss-group__grid { display: grid; gap: 16px; grid-template-columns: 1fr; }
@media (min-width: 768px) { .sv-gloss-group__grid { grid-template-columns: repeat(2, 1fr); } }
.sv-gloss-term-card { padding: 20px; background: #0f1d32; border-radius: 16px; text-decoration: none; transition: transform 200ms, box-shadow 200ms; position: relative; overflow: hidden; }
.sv-gloss-term-card::before { content: ''; position: absolute; inset: 0; border-radius: 16px; padding: 2px; background: transparent; -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; transition: background 300ms; }
.sv-gloss-term-card:hover::before { background: linear-gradient(135deg, #00d782, #ffd700); }
.sv-gloss-term-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,215,130,0.1); }
.sv-gloss-term-card__term { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 700; color: #fff; margin-bottom: 6px; }
.sv-gloss-term-card__def { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.6; }
.sv-gloss-term-card__cat { display: inline-block; margin-top: 10px; font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 12px; background: rgba(0,215,130,0.1); color: #00d782; }
.sv-gloss-empty { text-align: center; padding: 48px 0; font-family: 'Inter', sans-serif; color: #94a3b8; }
</style>
